import {
  type CSSProperties,
  FC,
  Fragment,
  memo,
  type RefObject,
  useEffect,
  useState,
} from 'react';
import { Check, Copy, RefreshCw, Share2, XIcon } from 'lucide-react';
import QrGenerator from '../ui/QrGenerator';
import useSessionFacilitators from '../../hooks/useSessionFacilitators';
import PrimaryActionButton from '../ui/PrimaryActionButton';

interface StartSessionModalProps {
  modalId: string;
  title: string;
  guideTargetRef?: RefObject<HTMLDialogElement | null>;
  sessionId: number | null;
  isOpen: boolean;
  onStartSession?: () => void;
  onCancel?: () => void;
  busy?: boolean;
  error?: string | null;
  cancelled?: boolean;
}

const facilitatorTabs = [
  { status: 'approved', label: 'Approved' },
  { status: 'pending', label: 'Pending' },
  { status: 'rejected', label: 'Rejected' },
] as const;

export const FacilitatorAccess = ({
  sessionId,
  isOpen,
  id: modalId,
  inline = false,
  onEnded,
  onCountsChange,
}: {
  sessionId: number | null;
  isOpen: boolean;
  id: string;
  inline?: boolean;
  onEnded?: (ended: boolean) => void;
  onCountsChange?: (counts: { approved: number; pending: number }) => void;
}) => {
  const { management, loading, busy, error, ended, offline, refresh, mutate } =
    useSessionFacilitators(sessionId, isOpen);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [copyError, setCopyError] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  useEffect(() => {
    onEnded?.(ended);
  }, [ended, onEnded]);
  const url =
    !error && !offline && !ended && management?.enabled ? management.url : null;
  const shareAvailable = typeof navigator.share === 'function';
  const disabled = busy || offline || ended || Boolean(error);
  const pending =
    management?.devices.filter((device) => device.status === 'pending') || [];
  const approvedCount =
    management?.devices.filter((device) => device.status === 'approved')
      .length ?? 0;
  useEffect(() => {
    onCountsChange?.({ approved: approvedCount, pending: pending.length });
  }, [approvedCount, onCountsChange, pending.length]);
  useEffect(() => {
    setCopiedUrl(null);
    setCopyError(false);
  }, [sessionId, isOpen, url]);
  useEffect(() => {
    setActiveTab('pending');
  }, [sessionId, isOpen]);
  useEffect(() => {
    if (!copiedUrl) return undefined;
    const timer = window.setTimeout(() => setCopiedUrl(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copiedUrl]);
  const copy = async () => {
    if (!url) return;
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
    } catch {
      setCopyError(true);
    }
  };
  const share = async () => {
    if (!url) return;
    if (!shareAvailable) {
      await copy();
      return;
    }
    try {
      await navigator.share({ title: 'Facilitator link', url });
    } catch (shareError) {
      if (
        shareError instanceof DOMException &&
        shareError.name === 'AbortError'
      )
        return;
      await copy();
    }
  };
  return (
    <section
      className={inline ? 'flex flex-col gap-3 mt-4' : 'flex flex-col gap-3'}
      aria-labelledby={`${modalId}-facilitators`}
    >
      <h2
        id={`${modalId}-facilitators`}
        className="text-base font-semibold text-gray-900 flex gap-2 items-center"
      >
        {inline ? 'Facilitators' : 'Invite Facilitators'}
      </h2>
      {!inline && (
        <p className="text-sm text-gray-600">
          Share this link with co-facilitators. They can only join after you
          approve them.
        </p>
      )}
      {loading && <p role="status">Loading facilitators...</p>}
      {offline && (
        <p role="status" className="text-amber-700">
          You&apos;re offline. Reconnect to manage facilitators.
        </p>
      )}
      {ended && (
        <p role="status">This session has ended or is no longer available.</p>
      )}
      {error && !ended && (
        <div
          role="alert"
          className="text-red-700 flex flex-wrap items-center gap-2"
        >
          {error}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={refresh}
            disabled={busy || offline}
            aria-label="Retry facilitator access"
            title="Retry facilitator access"
          >
            {inline ? 'Retry' : <RefreshCw size={18} />}
          </button>
        </div>
      )}
      {management && !ended && (
        <label
          htmlFor={`${modalId}-link-enabled`}
          className="flex justify-between gap-3 items-center text-sm"
        >
          Allow facilitator requests
          <input
            id={`${modalId}-link-enabled`}
            type="checkbox"
            className="toggle toggle-primary"
            checked={management.enabled}
            disabled={disabled}
            onChange={(event) => mutate('setLinkEnabled', event.target.checked)}
          />
        </label>
      )}
      <div className="p-4 bg-base-200">
        {management && !ended && (
          <div
            className={
              inline
                ? 'grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-6'
                : 'contents'
            }
          >
            <div
              className={inline ? 'flex flex-col gap-3 min-w-0' : 'contents'}
            >
              <div id="test-me" className="flex flex-row gap-4">
                {url && (
                  <div
                    className="ph-no-capture flex flex-col items-center gap-3"
                    data-ph-no-capture
                    data-ph-mask
                  >
                    <div
                      role="img"
                      className="p-2 bg-white"
                      aria-label="Facilitator invitation QR code"
                    >
                      <QrGenerator url={url} />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline bg-white btn-sm"
                        disabled={disabled}
                        onClick={share}
                        aria-label={
                          copiedUrl === url
                            ? 'Link copied'
                            : `${shareAvailable ? 'Share' : 'Copy'} facilitator link`
                        }
                        title={`${shareAvailable ? 'Share' : 'Copy'} facilitator link`}
                      >
                        {!inline && copiedUrl === url && <Check size={18} />}
                        {!inline && copiedUrl !== url && shareAvailable && (
                          <Share2 size={18} />
                        )}
                        {!inline && copiedUrl !== url && !shareAvailable && (
                          <Copy size={18} />
                        )}
                        {copiedUrl === url
                          ? 'Copied'
                          : `${shareAvailable ? 'Share' : 'Copy'} Link`}
                      </button>
                      <button
                        type="button"
                        className={
                          inline
                            ? 'btn btn-primary btn-sm'
                            : 'btn btn-ghost btn-sm'
                        }
                        disabled={disabled}
                        aria-label="Regenerate facilitator link"
                        title="Regenerate facilitator link"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Replace the shared link and cancel pending requests? Approved facilitators keep access.'
                            )
                          )
                            mutate('regenerateLink');
                        }}
                      >
                        {inline ? 'Regenerate Link' : <RefreshCw size={18} />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  {!management.enabled && (
                    <p className="text-sm text-gray-600">
                      Facilitator requests are off. Facilitators you already
                      approved can still join.
                    </p>
                  )}
                  {management.enabled && !management.url && !error && (
                    <p role="status">
                      The invite link couldn&apos;t be loaded. Try again.
                    </p>
                  )}
                  {copyError && (
                    <p role="alert" className="text-red-700 text-sm">
                      Could not copy the facilitator link.
                    </p>
                  )}
                  <div className="flex flex-col gap-3 min-w-0">
                    <div
                      role="tablist"
                      aria-label="Facilitator access status"
                      className="tabs tabs-border tabs-sm gap-2"
                    >
                      {facilitatorTabs.map(({ status, label }) => {
                        const devices = management.devices.filter(
                          (device) =>
                            device.status === status ||
                            (status === 'rejected' &&
                              device.status === 'revoked')
                        );
                        const tabId = `${modalId}-facilitators-${status}`;
                        return (
                          <Fragment key={status}>
                            <input
                              type="radio"
                              name={`${modalId}-facilitator-status`}
                              id={tabId}
                              role="tab"
                              style={{ '--tab-p': '0px' } as CSSProperties}
                              className="tab"
                              aria-label={`${label} (${devices.length})`}
                              aria-selected={activeTab === status}
                              aria-controls={`${tabId}-panel`}
                              checked={activeTab === status}
                              onChange={() => setActiveTab(status)}
                            />
                            <div
                              id={`${tabId}-panel`}
                              role="tabpanel"
                              aria-labelledby={tabId}
                              className="tab-content pt-3"
                              hidden={activeTab !== status}
                            >
                              {!devices.length && (
                                <p className="text-sm text-gray-600">
                                  No {label.toLowerCase()} facilitators.
                                </p>
                              )}
                              <ul
                                className="divide-y divide-gray-200 ph-no-capture"
                                data-ph-no-capture
                                data-ph-mask
                              >
                                {devices.map((device) => (
                                  <li
                                    key={device.id}
                                    className="py-3 flex flex-wrap items-center gap-3"
                                  >
                                    <div className="min-w-0 flex-1 basis-40 break-words">
                                      <p className="font-medium">
                                        {device.name}
                                      </p>
                                      {status === 'pending' ? (
                                        <p className="text-sm">
                                          Verification code:{' '}
                                          <strong>
                                            {device.verificationCode}
                                          </strong>
                                        </p>
                                      ) : (
                                        <p className="text-xs text-gray-600">
                                          {device.verificationCode} -{' '}
                                          {device.status}
                                        </p>
                                      )}
                                    </div>
                                    {status === 'pending' ? (
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          className="btn btn-primary btn-sm"
                                          disabled={disabled}
                                          onClick={() =>
                                            mutate('approve', device.id)
                                          }
                                          aria-label={`Approve ${device.name}`}
                                        >
                                          Approve
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-outline btn-sm"
                                          disabled={disabled}
                                          onClick={() =>
                                            mutate('reject', device.id)
                                          }
                                          aria-label={`Reject ${device.name}`}
                                        >
                                          Reject
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-outline btn-sm"
                                        disabled={disabled}
                                        onClick={() => {
                                          if (
                                            window.confirm(
                                              device.status === 'approved'
                                                ? `Remove ${device.name}'s access to this session?`
                                                : `Allow ${device.name} to join this session again?`
                                            )
                                          )
                                            mutate(
                                              device.status === 'approved'
                                                ? 'revoke'
                                                : 'restore',
                                              device.id
                                            );
                                        }}
                                      >
                                        {device.status === 'approved'
                                          ? 'Remove Access'
                                          : 'Allow Again'}
                                      </button>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {busy && (
          <p role="status" className="text-sm">
            Saving...
          </p>
        )}
      </div>
    </section>
  );
};

const StartSessionModal: FC<StartSessionModalProps> = ({
  modalId,
  title,
  guideTargetRef,
  sessionId,
  isOpen,
  onStartSession = () => {},
  onCancel = () => {},
  busy = false,
  error,
  cancelled = false,
}) => {
  const [ended, setEnded] = useState(false);
  useEffect(() => {
    setEnded(false);
  }, [sessionId, isOpen]);
  const cancel = () => {
    if (busy) return;
    onCancel();
  };
  return (
    <dialog
      ref={guideTargetRef}
      data-reguide-target="SCENARIO_LAUNCH"
      id={modalId}
      className="modal"
      aria-labelledby={`${modalId}-title`}
      onCancel={(event) => {
        event.preventDefault();
        cancel();
      }}
      onClose={(event) => {
        if (isOpen && !event.currentTarget.open) cancel();
      }}
    >
      <article className="modal-box flex flex-col gap-5 max-w-2xl bg-base-100 p-5 sm:p-8 max-h-[90dvh] overflow-y-auto">
        <header className="flex justify-between items-start gap-3">
          <h1
            id={`${modalId}-title`}
            className="text-2xl font-bold text-gray-900 min-w-0 break-words"
          >
            {title}
          </h1>
          <form method="dialog" className="m-0 shrink-0">
            <button
              type="button"
              onClick={cancel}
              className="btn btn-ghost text-gray-400 hover:text-gray-500 focus:outline-none"
              aria-label="Close modal"
              disabled={busy}
            >
              <XIcon size={20} />
            </button>
          </form>
        </header>
        <FacilitatorAccess
          sessionId={sessionId}
          isOpen={isOpen}
          id={modalId}
          onEnded={setEnded}
        />
        {error && (
          <p role="alert" className="text-error">
            {error}
          </p>
        )}
        {busy && <p role="status">Cancelling session...</p>}
        {/* <section className="flex items-start gap-3 bg-warning/10 border-l-4 border-warning p-4 rounded-box">
          <Volume2 className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-orange-800">
            <strong>Important:</strong> Share your computer audio if presenting
            via web conference
          </p>
        </section> */}
        <footer className="flex flex-col justify-between items-start sm:items-center gap-4 mt-4">
          <PrimaryActionButton
            type="button"
            onClick={onStartSession}
            disabled={ended || busy || cancelled}
            className="w-full h-10 px-4 py-6 text-base font-medium"
          >
            Go To Session
          </PrimaryActionButton>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className="btn w-full h-10 px-4 py-6 text-base font-medium"
          >
            Cancel
          </button>
        </footer>
      </article>
    </dialog>
  );
};

export default memo(StartSessionModal);
